import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SvgPause } from "../../../assets/icons/SvgPause";
import { SvgPlay } from "../../../assets/icons/SvgPlay";

const BAR_COUNT = 32;

// Играет одно сообщение: запуск другого ставит предыдущее на паузу
let currentAudio: HTMLAudioElement | null = null;

interface IVoicePlayerProps {
  url: string;
  durationMs: number;
  waveform: Nullable<number[]>;
  /** dark — на тёмном пузыре (ответы поддержки), light — на светлом (сообщения пользователя) */
  variant: "dark" | "light";
  /** Входящее ещё ни разу не прослушано: точка рядом с длительностью */
  isUnlistened?: boolean;
  onFirstPlay?: () => void;
}

/** Столбики волны из реальных амплитуд записи (0–100) под фиксированное число столбиков */
export function getWaveformBars(waveform: Nullable<number[]>): number[] {
  if (!waveform?.length) {
    // Волны нет (приложение не прислало): ровный ритм вместо пустой дорожки
    return Array.from({ length: BAR_COUNT }, (_, i) => 30 + ((i * 37) % 40));
  }

  return Array.from({ length: BAR_COUNT }, (_, i) => {
    const from = Math.floor((i * waveform.length) / BAR_COUNT);
    const to = Math.max(
      from + 1,
      Math.floor(((i + 1) * waveform.length) / BAR_COUNT)
    );
    const peak = Math.max(...waveform.slice(from, to));
    return Math.min(100, Math.max(12, peak));
  });
}

export function formatVoiceDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
}

export const VoicePlayer: FC<IVoicePlayerProps> = memo(
  ({ url, durationMs, waveform, variant, isUnlistened, onFirstPlay }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const firstPlayReported = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentMs, setCurrentMs] = useState(0);
    const [totalMs, setTotalMs] = useState(durationMs);
    const [hasError, setHasError] = useState(false);

    const bars = useMemo(() => getWaveformBars(waveform), [waveform]);

    const destroyAudio = useCallback(() => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      if (currentAudio === audio) currentAudio = null;
      audioRef.current = null;
    }, []);

    // Ссылка обновилась (сообщения перечитали) — на следующем воспроизведении берём новую
    useEffect(() => {
      if (audioRef.current && audioRef.current.paused) {
        destroyAudio();
      }
    }, [url, destroyAudio]);

    useEffect(() => destroyAudio, [destroyAudio]);

    const ensureAudio = () => {
      if (audioRef.current) return audioRef.current;

      const audio = new Audio(url);
      audio.preload = "metadata";
      audio.addEventListener("timeupdate", () =>
        setCurrentMs(audio.currentTime * 1000)
      );
      audio.addEventListener("loadedmetadata", () => {
        if (Number.isFinite(audio.duration)) setTotalMs(audio.duration * 1000);
      });
      audio.addEventListener("play", () => setIsPlaying(true));
      audio.addEventListener("pause", () => setIsPlaying(false));
      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentMs(0);
        audio.currentTime = 0;
      });
      // Подписанная ссылка живёт сутки; при ошибке даём понять, что файл недоступен
      audio.addEventListener("error", () => {
        setIsPlaying(false);
        setHasError(true);
      });
      audioRef.current = audio;
      return audio;
    };

    const handleToggle = () => {
      const audio = ensureAudio();

      if (!audio.paused) {
        audio.pause();
        return;
      }

      if (currentAudio && currentAudio !== audio) currentAudio.pause();
      currentAudio = audio;
      setHasError(false);

      audio.play().catch(() => setHasError(true));

      if (!firstPlayReported.current) {
        firstPlayReported.current = true;
        onFirstPlay?.();
      }
    };

    const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (event.clientX - rect.left) / rect.width)
      );
      const audio = ensureAudio();
      const seek = () => {
        const duration = Number.isFinite(audio.duration)
          ? audio.duration * 1000
          : totalMs;
        audio.currentTime = (ratio * duration) / 1000;
        setCurrentMs(ratio * duration);
      };

      if (audio.readyState >= 1) seek();
      else audio.addEventListener("loadedmetadata", seek, { once: true });
    };

    const progress = totalMs > 0 ? Math.min(1, currentMs / totalMs) : 0;
    const isDark = variant === "dark";
    // Во время и после начала воспроизведения показываем сколько осталось
    const label = formatVoiceDuration(
      currentMs > 0 ? totalMs - currentMs : totalMs
    );

    return (
      <div className="flex items-center gap-[10px]">
        <button
          type="button"
          onClick={handleToggle}
          aria-label={isPlaying ? "Пауза" : "Воспроизвести"}
          className={`rounded-full flex h-[36px] w-[36px] shrink-0 items-center justify-center outline-none transition ${
            isDark ? "bg-primary text-black" : "bg-black text-white"
          } hover:opacity-80 focus:opacity-80`}
        >
          {isPlaying ? (
            <SvgPause className="h-[16px] w-[16px]" />
          ) : (
            <SvgPlay className="ml-[2px] h-[16px] w-[16px]" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div
            role="slider"
            aria-label="Перемотка"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            onClick={handleSeek}
            className="flex h-[28px] cursor-pointer items-center justify-between"
          >
            {bars.map((height, index) => (
              <span
                key={index}
                style={{
                  height: `${height}%`,
                  ...(index / BAR_COUNT >= progress && isDark
                    ? { backgroundColor: "rgba(255, 255, 255, 0.35)" }
                    : {}),
                }}
                className={`rounded-full w-[3px] ${
                  index / BAR_COUNT < progress
                    ? "bg-primary"
                    : isDark
                    ? ""
                    : "bg-graySecond"
                }`}
              />
            ))}
          </div>
          <div className="mt-[2px] flex items-center text-xxs">
            {isUnlistened && (
              <span
                aria-label="Не прослушано"
                className="rounded-full mr-[5px] inline-block h-[7px] w-[7px] bg-primary"
              />
            )}
            <span className={isDark ? "text-white" : "text-gray"}>{label}</span>
            {hasError && (
              <span className="ml-[8px] text-red">
                Не удалось воспроизвести
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
);
